import React from 'react'
import PropTypes from 'prop-types'
import { Link } from 'react-router-dom'
import { connect } from 'react-redux'
import styled from '@emotion/styled'
import { cx } from 'emotion'
import { underlineFocus, FocusSpan } from './focus-styles'
import { getEmbeddedViewFlag } from 'portal-modules/common/utils/get-embedded-flag'
export const StyledButton = styled('button')`
  ${({ size, theme, autoHeight, noMarginTop, noMarginRight, noBorderRadius }) => {
    switch (size) {
      case 'large':
        return {
          border: 0,
          borderRadius: '100px',
          fontFamily: theme.fonts.body,
          fontSize: '16px',
          padding: '20px',
          lineHeight: '16px',
          textAlign: 'center',
          letterSpacing: '0.5px',
          width: '300px',
          height: '64px!important',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.24)',
          marginRight: noMarginRight ? '0' : '24px',
          marginTop: noMarginTop ? '0' : '16px',
        }
      case 'medium':
        return {
          border: 0,
          height: autoHeight ? 'auto' : '48px',
          borderRadius: noBorderRadius ? '0' : '100px',
          padding: '14px 28px',
          marginRight: noMarginRight ? '0' : '16px',
          marginTop: '16px',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.24)',
          minWidth: '100px',
          width: 'max-content',
          fontSize: noMarginTop ? '0' : '16px',
        }
      case 'small':
        return {
          border: 0,
          height: '32px',
          borderRadius: '100px',
          padding: '8px 16px',
          marginRight: '16px',
          marginTop: noMarginTop ? '0' : '8px',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.24)',
          minWidth: '100px',
          width: 'auto',
          fontSize: '13px',
        }
      case 'mini':
        return {
          border: 0,
          height: '24px',
          borderRadius: '100px',
          padding: '4px 16px',
          marginRight: '16px',
          marginTop: '8px',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.24)',
          minWidth: '100px',
          width: 'auto',
          fontSize: '13px',
        }
      case 'textIcon':
        return {
          stroke: theme.gray.light,
          border: `1px solid ${theme.gray.light}`,
          color: theme.gray.gray5,
          height: '32px',
          borderRadius: '100px',
          padding: '8px',
          marginRight: '16px',
          width: 'max-content',
          fontSize: '13px',
          backgroundColor: theme.backgroundColor.white,
        }
      default:
        return {
          border: '0',
          borderRadius: '100px',
          height: autoHeight ? 'auto' : '48px',
          fontFamily: theme.fonts.body,
          minWidth: '100px',
          width: 'max-content',
          fontSize: '16px',
          textAlign: 'center',
          textDecoration: 'none',
          transition: 'all 250ms ease-out',
          textTransform: 'capitalize',
          padding: '14px 28px',
        }
    }
  }}
  ${({ appearance, isNormalButton, theme }) => {
    const themeButtonTextColor = theme.button_primary.color
    switch (appearance) {
      case 'primary':
        return {
          backgroundColor: theme.button_primary.background,
          color: getEmbeddedViewFlag()
            ? theme.embedded.button_text
            : themeButtonTextColor,
          fontWeight: 700,
        }

      case 'secondary':
        return {
          backgroundColor: getEmbeddedViewFlag()
            ? theme.embedded.button_text
            : isNormalButton || !theme.mode.isDark
              ? 'transparent'
              : theme.mode.background_color,
          color: getEmbeddedViewFlag()
            ? theme.button_primary.background
            : themeButtonTextColor,
          border: isNormalButton
            ? 0
            : `1px solid ${theme.button_secondary.border}`,
          fontWeight: 700,
        }

      case 'action':
        return {
          backgroundColor: isNormalButton
            ? 'transparent'
            : theme.button_secondary.background,
          color: themeButtonTextColor,
          border: isNormalButton
            ? `1px solid ${theme.gray.gray2}`
            : `1px solid ${theme.button_secondary.border_color}`,
          fontWeight: 400,
        }

      case 'tertiary':
        return {
          backgroundColor: theme.icon.primary,
          color: theme.gray.gray5,
          border: `1px solid ${theme.button_secondary.border_color}`,
          fontWeight: 700,
        }
    }
  }}
  ${({ isDeleteButton, theme }) => {

    if (isDeleteButton) {
      return `
      border: 1px solid ${theme.button_delete.borderColor};
      box-shadow: inset 0.75px 0.5px 2px rgba(255, 255, 255, 0.08);
      filter: drop-shadow(1px 1px 4px rgba(118, 118, 118, 0.06));
      color: ${theme.button_delete.textColor};
    `
    }
  }}

  white-space: nowrap; /* Prevents wrapping */
  overflow: hidden; /* Hides any overflowed content */
  text-overflow: ellipsis; /* Displays an ellipsis (...) to indicate text overflow */
  &:hover {
    overflow: visible; /* Show overflow content on hover */
    white-space: normal; /* Allow text to wrap */
  }

  @media only screen and (max-width: 500px) {
    /* Mobile specific styles */
    width: ${({ size }) =>
      size === 'small' || size === 'mini' ? 'auto' : '100%'};
    height: ${({ size }) =>
      size === 'mini' ? '24px' : size === 'small' ? '32px' : '64px'};
    padding: ${({ size }) =>
      size === 'mini' ? '4px 16px' : size === 'small' ? '8px 16px' : '20px'};
    font-size: ${({ size }) =>
      size === 'small' || size === 'mini' ? '13px' : '16px'};
    &.action-link {
      height: 32px;
      padding: 9px 16px 10px;
      font-size: 13px;
      width: max-content;
      margin: ${({ mediaMargin }) => (mediaMargin ? mediaMargin : 'none')};
    }
    &.medium-full-width {
      width: max-content;
      height: 48px;
      padding: 14px 28px;
    }
  }

  &.selected {
    background-color: ${({ theme }) => theme.button_selected.background};
    color: ${({ theme }) => theme.button_selected.color};
  }

  &.full-width {
    width: 100%;
    display: block;
  }

  &[disabled] {
    opacity: 0.5;
  }

  ${underlineFocus}
`

const StyledLink = StyledButton.withComponent(Link)

const Button = ({
  children,
  className,
  disabled,
  fullWidth,
  onClick,
  offline,
  selected,
  to,
  appearance,
  slim,
  isActionLink,
  mediaMargin,
  ...rest
}) =>
  to ? (
    <StyledLink
      className={cx({
        'full-width': fullWidth,
        selected: selected,
        'action-link': isActionLink,
        [className]: !!className,
      })}
      disabled={disabled}
      mediaMargin={mediaMargin}
      to={to}
      appearance={appearance}
      slim={slim ? 1 : 0}
    >
      <FocusSpan>{children}</FocusSpan>
    </StyledLink>
  ) : (
    <StyledButton
      className={cx({
        'full-width': fullWidth,
        selected: selected,
        [className]: !!className,
      })}
      disabled={offline || disabled}
      onClick={!offline && !disabled ? onClick : null}
      appearance={appearance}
      slim={slim ? 1 : 0}
      {...rest}
    >
      <FocusSpan>{children}</FocusSpan>
    </StyledButton>
  )

Button.propTypes = {
  /** Type of button. */
  appearance: PropTypes.oneOf(['primary', 'secondary', 'action']),
  /** Text which is displayed on Button. */
  children: PropTypes.any.isRequired,
  className: PropTypes.string,
  /** Determines if the button is disabled or not. */
  disabled: PropTypes.bool,
  /** If true, the button will always be full-width */
  fullWidth: PropTypes.bool,
  /** Function called when button is clicked. */
  onClick: PropTypes.func,
  offline: PropTypes.bool,
  /** If true apply selected appearance to the button */
  selected: PropTypes.bool,
  /** URL. If value is provided, button will behave as react dom router link */
  to: PropTypes.string,
  /** Reduces font-size and padding of the button. */
  slim: PropTypes.bool,
  type: PropTypes.string,
  hasWidth: PropTypes.bool,
  noMarginRight: PropTypes.bool,
  mediaMargin: PropTypes.string,
  isActionLink: PropTypes.bool,
}

Button.defaultProps = {
  disabled: false,
  fullWidth: false,
  selected: false,
  slim: false,
  type: 'button',
  hasWidth: false,
  isActionLink: false,
  noMarginRight: false,
}

export default connect(({ offline }) => ({ offline }))(Button)